import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams, Link, useNavigate } from 'react-router-dom';
import { useStorefrontAuth } from '../auth/context/StorefrontAuthContext';
import { getLandingNavigationPages, normalizeLandingPageSettings } from '../utils/custom-pages/pageConfig';
import {
  formatPhoneForDisplay,
  getBusinessAddress,
  getBusinessPhone,
  getBusinessSocialLinks,
  getPhoneTelHref
} from '../utils/contactInfo';
import { getStorefrontAccountHomePath } from '../storefront-client-portal/utils/clientPortalPaths';

/** Matches `.header-account-menu` min-width in LandingPage.css */
const HEADER_ACCOUNT_MENU_MIN_WIDTH = 168;

const SocialIcon = ({ platformKey }) => {
  switch (platformKey) {
    case 'instagram':
      return (
        <svg aria-hidden="true" viewBox="0 0 24 24" className="header-subbar__icon">
          <path
            fill="currentColor"
            d="M7.75 2h8.5A5.75 5.75 0 0 1 22 7.75v8.5A5.75 5.75 0 0 1 16.25 22h-8.5A5.75 5.75 0 0 1 2 16.25v-8.5A5.75 5.75 0 0 1 7.75 2Zm0 1.8A3.95 3.95 0 0 0 3.8 7.75v8.5a3.95 3.95 0 0 0 3.95 3.95h8.5a3.95 3.95 0 0 0 3.95-3.95v-8.5a3.95 3.95 0 0 0-3.95-3.95h-8.5Zm8.95 1.35a1.1 1.1 0 1 1 0 2.2 1.1 1.1 0 0 1 0-2.2ZM12 6.7A5.3 5.3 0 1 1 6.7 12 5.31 5.31 0 0 1 12 6.7Zm0 1.8A3.5 3.5 0 1 0 15.5 12 3.5 3.5 0 0 0 12 8.5Z"
          />
        </svg>
      );
    case 'facebook':
      return (
        <svg aria-hidden="true" viewBox="0 0 24 24" className="header-subbar__icon">
          <path fill="currentColor" d="M13.5 21v-7.2H16l.38-2.95H13.5V8.97c0-.85.24-1.43 1.46-1.43h1.56V4.9c-.27-.04-1.2-.1-2.29-.1-2.27 0-3.83 1.39-3.83 3.95v2.1H8v2.95h2.4V21h3.1Z" />
        </svg>
      );
    case 'linkedin':
      return (
        <svg aria-hidden="true" viewBox="0 0 24 24" className="header-subbar__icon">
          <path fill="currentColor" d="M6.94 8.5a1.81 1.81 0 1 1 0-3.62 1.81 1.81 0 0 1 0 3.62ZM5.39 20V10.02H8.5V20H5.39Zm5.06 0V10.02h2.98v1.36h.04c.42-.79 1.44-1.63 2.96-1.63 3.16 0 3.74 2.08 3.74 4.79V20h-3.11v-4.84c0-1.15-.02-2.63-1.6-2.63-1.61 0-1.86 1.25-1.86 2.55V20h-3.15Z" />
        </svg>
      );
    case 'tiktok':
      return (
        <svg aria-hidden="true" viewBox="0 0 24 24" className="header-subbar__icon">
          <path fill="currentColor" d="M14.62 3c.2 1.67 1.14 3.05 2.62 3.82V9.3a6.87 6.87 0 0 1-2.62-.62v5.46a5.16 5.16 0 1 1-5.16-5.16c.23 0 .46.02.68.05v2.56a2.66 2.66 0 1 0 1.98 2.57V3h2.5Z" />
        </svg>
      );
    case 'youtube':
      return (
        <svg aria-hidden="true" viewBox="0 0 24 24" className="header-subbar__icon">
          <path fill="currentColor" d="M21.58 7.19a2.9 2.9 0 0 0-2.04-2.05C17.75 4.67 12 4.67 12 4.67s-5.75 0-7.54.47A2.9 2.9 0 0 0 2.42 7.2 30.3 30.3 0 0 0 2 12a30.3 30.3 0 0 0 .42 4.81 2.9 2.9 0 0 0 2.04 2.05c1.79.47 7.54.47 7.54.47s5.75 0 7.54-.47a2.9 2.9 0 0 0 2.04-2.05A30.3 30.3 0 0 0 22 12a30.3 30.3 0 0 0-.42-4.81ZM10.08 15.01V8.99L15.27 12l-5.19 3.01Z" />
        </svg>
      );
    case 'x':
      return (
        <svg aria-hidden="true" viewBox="0 0 24 24" className="header-subbar__icon">
          <path fill="currentColor" d="M18.9 4H21l-4.59 5.24L21.8 20h-4.23l-3.31-6.22L8.8 20H6.69l4.91-5.6L6.4 4h4.34l2.99 5.62L18.9 4Zm-.74 14.74h1.17L10.11 5.2H8.86l9.3 13.54Z" />
        </svg>
      );
    default:
      return (
        <span aria-hidden="true" className="header-subbar__icon header-subbar__icon--fallback">
          {platformKey.slice(0, 1).toUpperCase()}
        </span>
      );
  }
};

const PhoneIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" className="header-subbar__icon">
    <path
      fill="currentColor"
      d="M6.62 10.79a15.54 15.54 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.02-.24 11.36 11.36 0 0 0 3.57.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.49a1 1 0 0 1 1 1 11.36 11.36 0 0 0 .57 3.57 1 1 0 0 1-.24 1.02l-2.2 2.2Z"
    />
  </svg>
);

const LocationIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" className="header-subbar__icon">
    <path
      fill="currentColor"
      d="M12 2a7 7 0 0 0-7 7c0 5.07 6.05 12.05 6.3 12.34a.92.92 0 0 0 1.4 0C12.95 21.05 19 14.07 19 9a7 7 0 0 0-7-7Zm0 9.5A2.5 2.5 0 1 1 14.5 9 2.5 2.5 0 0 1 12 11.5Z"
    />
  </svg>
);

/** Sign-in CTA on small screens (text label is shown from md breakpoint up in CSS). */
const LoginIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" className="header-nav-auth__sign-in-svg">
    <path
      fill="currentColor"
      d="M11 7 9.6 8.4 12 11H2v2h10l-2.6 2.6L11 17l5-5-5-5Zm9-2h-2v14h2V5z"
    />
  </svg>
);

const ChevronDownIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" className="header-mobile-pages-toggle__icon">
    <path fill="currentColor" d="M6.7 9.3a1 1 0 0 1 1.4 0L12 13.2l3.9-3.9a1 1 0 1 1 1.4 1.4l-4.6 4.6a1 1 0 0 1-1.4 0L6.7 10.7a1 1 0 0 1 0-1.4Z" />
  </svg>
);

const MenuIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" className="header-mobile-pages-toggle__menu-icon">
    <path fill="currentColor" d="M4 7a1 1 0 0 1 1-1h14a1 1 0 1 1 0 2H5A1 1 0 0 1 4 7Zm0 5a1 1 0 0 1 1-1h14a1 1 0 1 1 0 2H5a1 1 0 0 1-1-1Zm1 4a1 1 0 1 0 0 2h14a1 1 0 1 0 0-2H5Z" />
  </svg>
);

const HeaderNavLink = ({
  previewMode,
  to,
  className,
  isActive,
  onNavigate,
  onClick,
  children
}) => {
  const resolvedClassName = `${className}${isActive ? ' is-active' : ''}`;

  if (previewMode) {
    return (
      <a
        href="#"
        className={resolvedClassName}
        onClick={(event) => {
          event.preventDefault();
          onClick?.(event);
          onNavigate?.();
        }}
      >
        {children}
      </a>
    );
  }

  return (
    <Link to={to} className={resolvedClassName} onClick={onClick}>
      {children}
    </Link>
  );
};

const Header = ({
  companyData,
  previewMode = false,
  previewCompanyUrl = '',
  previewPageId = 'home',
  onPreviewNavigate = () => {}
}) => {
  const { companyUrl: routeCompanyUrl } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    storefrontClient,
    hasPortalAccess,
    isAuthenticated,
    loading: authLoading,
    signOutStorefront
  } = useStorefrontAuth();
  /** Storefront account / sign-in — not tied to legacy `/portal/` settings */
  const showAuthEntry = true;
  const normalizedSettings = normalizeLandingPageSettings(companyData?.landingPageSettings, companyData);
  const navigationPages = getLandingNavigationPages(companyData?.landingPageSettings, companyData);

  const [menuOpen, setMenuOpen] = useState(false);
  const [mobilePagesOpen, setMobilePagesOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [accountMenuPosition, setAccountMenuPosition] = useState(null);

  const accountButtonRef = useRef(null);

  const companyUrl = previewMode ? (previewCompanyUrl || 'preview') : routeCompanyUrl;
  const businessPhone = getBusinessPhone(companyData);
  const businessPhoneDisplay = businessPhone ? formatPhoneForDisplay(businessPhone) : '';
  const businessAddress = getBusinessAddress(companyData);
  const socialLinks = getBusinessSocialLinks(companyData);
  const hasInfoBar = Boolean(businessPhone || businessAddress || socialLinks.length > 0);
  const handleToggleMenu = () => {
    setAccountMenuOpen(false);
    setMenuOpen((currentOpen) => !currentOpen);
  };
  const handleCloseMenu = () => {
    setMenuOpen(false);
    setAccountMenuOpen(false);
  };
  const homeHref = `/book/${companyUrl}`;
  const isActivePath = (path) => location.pathname === path;
  const isPreviewPageActive = (pageId) => previewPageId === pageId;
  const isNavItemActive = (pageId, path) => (
    previewMode ? isPreviewPageActive(pageId) : isActivePath(path)
  );
  const handlePreviewNavigate = (pageId) => {
    handleCloseMenu();
    handleMobilePagesClose();
    onPreviewNavigate(pageId);
  };
  const handleBrandClick = (event) => {
    handleCloseMenu();
    setMobilePagesOpen(false);

    if (previewMode) {
      event.preventDefault();
      handlePreviewNavigate('home');
      return;
    }

    if (location.pathname === homeHref) {
      event.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  const showServicesLink = normalizedSettings.navigation.showHome;
  const totalMobileNavLinks = (showServicesLink ? 1 : 0) + navigationPages.length;
  const useMobilePagesDropdown = totalMobileNavLinks >= 3;
  const handleMobilePagesToggle = () => {
    setAccountMenuOpen(false);
    setMobilePagesOpen((currentOpen) => !currentOpen);
  };
  const handleMobilePagesClose = () => setMobilePagesOpen(false);
  const handleToggleAccountMenu = () => {
    setMenuOpen(false);
    setMobilePagesOpen(false);
    setAccountMenuOpen((prev) => !prev);
  };

  const handleCloseAccountMenu = () => setAccountMenuOpen(false);

  const handleGoToAccount = () => {
    handleCloseMenu();
    handleMobilePagesClose();
    handleCloseAccountMenu();
    navigate(getStorefrontAccountHomePath(companyUrl));
  };

  const handleSignOutFromMenu = async () => {
    handleCloseMenu();
    handleMobilePagesClose();
    handleCloseAccountMenu();
    try {
      await signOutStorefront();
      navigate(`/book/${companyUrl}`, { replace: true });
    } catch (e) {
      console.error(e);
    }
  };

  const handleOpenAuth = () => {
    handleCloseMenu();
    handleMobilePagesClose();
    handleCloseAccountMenu();
    if (hasPortalAccess) {
      navigate(getStorefrontAccountHomePath(companyUrl));
      return;
    }
    navigate(`/book/${companyUrl}/login`, {
      state: {
        from: location.pathname
      }
    });
  };
  const clientFullNameForA11y = useMemo(() => {
    if (!storefrontClient) return 'Your account';
    const full = `${storefrontClient.firstName || ''} ${storefrontClient.lastName || ''}`.trim();
    return full || storefrontClient.name || storefrontClient.email || 'Your account';
  }, [storefrontClient]);

  const clientInitials = useMemo(() => {
    if (!storefrontClient) return '?';
    const a = (storefrontClient.firstName || '').charAt(0);
    const b = (storefrontClient.lastName || '').charAt(0);
    if (a || b) return `${a}${b}`.toUpperCase();
    const n = storefrontClient.name || storefrontClient.email || '?';
    return n.slice(0, 2).toUpperCase();
  }, [storefrontClient]);

  const showSignedInChip = Boolean(!authLoading && isAuthenticated && storefrontClient);

  useLayoutEffect(() => {
    if (!accountMenuOpen || !showSignedInChip) {
      setAccountMenuPosition(null);
      return undefined;
    }

    const placeMenu = () => {
      const el = accountButtonRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let left = rect.right - HEADER_ACCOUNT_MENU_MIN_WIDTH;
      left = Math.max(8, Math.min(left, vw - HEADER_ACCOUNT_MENU_MIN_WIDTH - 8));

      let top = rect.bottom + 8;
      const estMenuHeight = 120;
      if (top + estMenuHeight > vh - 8 && rect.top > estMenuHeight + 16) {
        top = rect.top - estMenuHeight - 8;
      }
      top = Math.max(8, top);

      setAccountMenuPosition({ top, left });
    };

    placeMenu();
    window.addEventListener('resize', placeMenu);
    window.addEventListener('scroll', placeMenu, true);
    return () => {
      window.removeEventListener('resize', placeMenu);
      window.removeEventListener('scroll', placeMenu, true);
    };
  }, [accountMenuOpen, showSignedInChip]);

  useEffect(() => {
    if (!accountMenuOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setAccountMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [accountMenuOpen]);

  useEffect(() => {
    if (previewMode) {
      return;
    }

    setAccountMenuOpen(false);
  }, [location.pathname, previewMode]);

  useEffect(() => {
    if (!previewMode) {
      return;
    }

    setAccountMenuOpen(false);
  }, [previewPageId, previewMode]);

  useEffect(() => {
    if (!showSignedInChip) setAccountMenuOpen(false);
  }, [showSignedInChip]);

  return (
    <header className="landing-header">
      <div className="container">
        <div className="header-content">
          {previewMode ? (
            <a
              href="#"
              className="header-branding"
              onClick={handleBrandClick}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <h1 className="landing-header__company-name">{companyData?.name || 'Booking'}</h1>
              {companyData?.logoUrl && (
                <img
                  src={companyData.logoUrl}
                  alt={companyData.name}
                  className="header-logo"
                />
              )}
            </a>
          ) : (
            <Link
              to={homeHref}
              className="header-branding"
              onClick={handleBrandClick}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <h1 className="landing-header__company-name">{companyData?.name || 'Booking'}</h1>
              {companyData?.logoUrl && (
                <img
                  src={companyData.logoUrl}
                  alt={companyData.name}
                  className="header-logo"
                />
              )}
            </Link>
          )}

          {socialLinks.length > 0 && (
            <div className="header-mobile-socials" aria-label="Social media links">
              {socialLinks.map((link) => (
                <a
                  key={link.key}
                  className="header-mobile-social"
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={link.label}
                  title={link.label}
                >
                  <SocialIcon platformKey={link.key} />
                </a>
              ))}
            </div>
          )}

          <button
            type="button"
            className={`header-menu-toggle${menuOpen ? ' open' : ''}`}
            onClick={handleToggleMenu}
            aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={menuOpen}
          >
            <span />
            <span />
            <span />
          </button>
          
          <div className="header-nav-row">
            <div className="header-nav-cluster">
              <nav className={`header-nav${menuOpen ? ' open' : ''}${useMobilePagesDropdown ? ' has-mobile-dropdown' : ''}`}>
                <div className="header-desktop-nav-links">
                  {showServicesLink && (
                    <HeaderNavLink
                      previewMode={previewMode}
                      to={homeHref}
                      className="nav-link"
                      isActive={isNavItemActive('home', homeHref)}
                      onNavigate={() => handlePreviewNavigate('home')}
                      onClick={handleCloseMenu}
                    >
                      Services
                    </HeaderNavLink>
                  )}
                  {navigationPages.map((page) => {
                    const pageHref = `/book/${companyUrl}/${page.slug}`;

                    return (
                      <HeaderNavLink
                        key={page.id}
                        previewMode={previewMode}
                        to={pageHref}
                        className="nav-link"
                        isActive={isNavItemActive(page.id, pageHref)}
                        onNavigate={() => handlePreviewNavigate(page.id)}
                        onClick={handleCloseMenu}
                      >
                        {page.title}
                      </HeaderNavLink>
                    );
                  })}
                </div>

                {useMobilePagesDropdown && (
                  <div className="header-mobile-pages">
                    <button
                      type="button"
                      className={`header-mobile-pages-toggle${mobilePagesOpen ? ' open' : ''}`}
                      onClick={handleMobilePagesToggle}
                      aria-expanded={mobilePagesOpen}
                      aria-label="Open pages menu"
                      title="Open pages menu"
                    >
                      <MenuIcon />
                      <ChevronDownIcon />
                    </button>
                    {mobilePagesOpen && (
                      <div className="header-mobile-pages-menu">
                        {showServicesLink && (
                          <HeaderNavLink
                            previewMode={previewMode}
                            to={homeHref}
                            className="header-mobile-pages-link"
                            isActive={isNavItemActive('home', homeHref)}
                            onNavigate={() => handlePreviewNavigate('home')}
                            onClick={handleMobilePagesClose}
                          >
                            Services
                          </HeaderNavLink>
                        )}
                        {navigationPages.map((page) => {
                          const pageHref = `/book/${companyUrl}/${page.slug}`;

                          return (
                            <HeaderNavLink
                              key={page.id}
                              previewMode={previewMode}
                              to={pageHref}
                              className="header-mobile-pages-link"
                              isActive={isNavItemActive(page.id, pageHref)}
                              onNavigate={() => handlePreviewNavigate(page.id)}
                              onClick={handleMobilePagesClose}
                            >
                              {page.title}
                            </HeaderNavLink>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </nav>
            </div>

            {(businessAddress || businessPhone) && (
              <div className="header-mobile-actions" aria-label="Quick actions">
                {businessAddress && (
                  <a
                    className="header-mobile-action"
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(businessAddress)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Find us"
                    title="Find us"
                  >
                    <LocationIcon />
                  </a>
                )}
                {businessPhone && (
                  <a
                    className="header-mobile-action"
                    href={getPhoneTelHref(businessPhone)}
                    aria-label="Call us"
                    title="Call us"
                  >
                    <PhoneIcon />
                  </a>
                )}
              </div>
            )}

            {showAuthEntry && !previewMode ? (
              <div className="header-nav-auth" aria-label="Account">
                {showSignedInChip ? (
                  <button
                    type="button"
                    ref={accountButtonRef}
                    className="header-account-chip"
                    onClick={handleToggleAccountMenu}
                    title={clientFullNameForA11y}
                    aria-label={`Your account (${clientFullNameForA11y})`}
                    aria-expanded={accountMenuOpen}
                    aria-haspopup="menu"
                  >
                    <span className="header-account-chip__avatar-wrap">
                      {storefrontClient?.avatarUrl ? (
                        <img src={storefrontClient.avatarUrl} alt="" />
                      ) : (
                        <span className="header-account-chip__initials">{clientInitials}</span>
                      )}
                    </span>
                  </button>
                ) : (
                  <button
                    type="button"
                    className="nav-link nav-link--sign-in header-nav-auth__sign-in"
                    onClick={handleOpenAuth}
                    aria-label="Sign in"
                    title="Sign in"
                  >
                    <span className="header-nav-auth__sign-in-label">Sign in</span>
                    <span className="header-nav-auth__sign-in-icon-wrap" aria-hidden="true">
                      <LoginIcon />
                    </span>
                  </button>
                )}
              </div>
            ) : null}
          </div>
        </div>
        {showSignedInChip && accountMenuOpen && (
          <>
            <div className="header-account-menu-backdrop" aria-hidden onClick={() => setAccountMenuOpen(false)} />
            {accountMenuPosition ? (
              <div
                className="header-account-menu"
                role="menu"
                aria-label="Your account"
                style={{ top: accountMenuPosition.top, left: accountMenuPosition.left }}
              >
                {hasPortalAccess ? (
                  <button type="button" className="header-account-menu__item" role="menuitem" onClick={handleGoToAccount}>
                    Account
                  </button>
                ) : null}
                <button
                  type="button"
                  className="header-account-menu__item header-account-menu__item--danger"
                  role="menuitem"
                  onClick={handleSignOutFromMenu}
                >
                  Sign out
                </button>
              </div>
            ) : null}
          </>
        )}
        {hasInfoBar && (
          <div className="header-subbar">
            <div
              className={`header-subbar__content${
                socialLinks.length === 0 ? ' header-subbar__content--no-socials' : ''
              }`}
            >
              {businessPhone && (
                <div className="header-subbar__slot header-subbar__slot--left">
                  <a className="header-subbar__item" href={getPhoneTelHref(businessPhone)}>
                    <PhoneIcon />
                    <span className="header-subbar__label">Phone</span>
                    <span>{businessPhoneDisplay}</span>
                  </a>
                </div>
              )}
              {socialLinks.length > 0 && (
                <div className="header-subbar__slot header-subbar__slot--center">
                  <div className="header-subbar__socials" aria-label="Social media links">
                    {socialLinks.map((link) => (
                      <a
                        key={link.key}
                        className="header-subbar__item header-subbar__item--social"
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={link.label}
                        title={link.label}
                      >
                        <SocialIcon platformKey={link.key} />
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {businessAddress && (
                <div className="header-subbar__slot header-subbar__slot--right">
                  <a
                    className="header-subbar__item"
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(businessAddress)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <LocationIcon />
                    <span className="header-subbar__label">Find us</span>
                    <span>{businessAddress}</span>
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;

